import Cookies from 'js-cookie';

/**
 * Cookie Manager - Secure cookie handling utilities
 *
 * Security: Secure flag enforced in production, SameSite configured per type,
 * proper cleanup on logout. HttpOnly cannot be set client-side.
 * See /docs/cookie-security.md for details.
 */

type CookieType = 'session' | 'preference' | 'functional';

interface CookieOptions {
  expires?: number;
  sameSite?: 'Strict' | 'Lax' | 'None';
  domain?: string;
  path?: string;
  secure?: boolean;
}

const getCookieOptions = (type: CookieType = 'functional'): CookieOptions => {
  const runtimeConfig = useRuntimeConfig();
  const isProduction = process.env.NODE_ENV === 'production';
  const isHttps =
    typeof window !== 'undefined'
      ? window.location.protocol === 'https:'
      : false;

  const expirationDays = type === 'session' ? 7 : 365;

  return {
    expires: expirationDays,
    sameSite: type === 'session' ? 'Strict' : 'Lax',
    domain: runtimeConfig.public.cookieDomain || undefined,
    path: '/',
    secure: isProduction || isHttps,
  };
};

/**
 * Set a cookie with appropriate security configuration
 * @param type - 'session' for auth tokens, 'preference' for settings, 'functional' for app state
 */
export const setCookie = (
  name: string,
  value: string | object,
  type: CookieType = 'functional'
) => {
  // Guard against accidental writes of actual nullish values; without this,
  // js-cookie would happily persist the literal string "undefined"/"null"
  // (via implicit coercion) and any later getCookie() would return it as if
  // it were a real value. The JSON-encoded form (e.g. JSON.stringify(null)
  // === 'null') is a legitimate caller-intended value and passes through.
  if (value === null || value === undefined) {
    return undefined;
  }
  const cookieValue = typeof value === 'object' ? JSON.stringify(value) : value;
  const options = getCookieOptions(type);
  return Cookies.set(name, cookieValue, options);
};

/**
 * Get a cookie with optional JSON parsing
 */
export const getCookie = (name: string, parseJson = true) => {
  const value = Cookies.get(name);
  if (!value) return null;

  if (parseJson) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  return value;
};

/**
 * Remove a cookie by attempting all possible domain/path combinations
 */
export const removeCookie = (name: string) => {
  const runtimeConfig = useRuntimeConfig();
  const domain = runtimeConfig.public.cookieDomain;

  // Remove with configured domain if available
  if (domain) {
    Cookies.remove(name, { path: '/', domain });
  }

  // Also try common variations to clean up any legacy cookies
  Cookies.remove(name, { path: '/' });

  // Try with window.location.hostname for cookies that might have been set with it
  if (typeof window !== 'undefined') {
    Cookies.remove(name, { path: '/', domain: window.location.hostname });

    // Try parent domain (e.g., .example.com from app.example.com)
    const hostParts = window.location.hostname.split('.');
    if (hostParts.length > 2) {
      const parentDomain = '.' + hostParts.slice(-2).join('.');
      Cookies.remove(name, { path: '/', domain: parentDomain });
    }
  }

  // Fallback for cookies set without any options
  Cookies.remove(name);
};

/**
 * Remove all authentication cookies on logout
 */
export const clearAllAuthCookies = () => {
  const authCookies = [
    'access_token',
    'selected_client_id',
    'bearer_token',
    // OAuth2 session cookies (see composables/useAuth.ts)
    'su_access_token',
    'su_refresh_token',
  ];

  authCookies.forEach(cookieName => {
    removeCookie(cookieName);
  });
};
